import { Document, Font } from "@react-pdf/renderer";
import React, { useMemo } from "react";
import robotoBold from "../../../fonts/roboto-700.ttf";
import robotoItalic from "../../../fonts/Roboto-Italic.ttf";
import roboto from "../../../fonts/roboto.ttf";
import { flattenObject } from "../../../lib/flattenObject";
import { YesNo } from "../../../lib/models/enums";
import { overrideLabelMap } from "../../../lib/models/override-label-map";
import { RentalCalculation } from "../../../lib/models/rental/rental-calculation.model";
import { RentalQuote, Scenario } from "../../../lib/models/rental/rental.model";
import { SFUserProfile } from "../../../lib/models/sf-user-profile.model";
import { Comment } from "../../../lib/types/CommentType";
import { PdfGenerationActions } from "../../../lib/types/PDFGenerationActions";
import { QuoteMetadata } from "../../../lib/types/QuoteMetadata";
import { bulletListToArray } from "../../../lib/utils";
import RentalCalculationSheet from "./RentalCalculationSheet";
import RentalDisclosureSheet from "./RentalDisclosureSheet";
import RentalInternalSelectionSheet from "./RentalInternalSelectionSheet";
import RentalSummarySheet from "./RentalSummarySheet";

export interface RentalTermSheetProps {
  quote: RentalQuote;
  calculation: RentalCalculation;
  scenarios: Scenario[];
  action: PdfGenerationActions;
  userProfile: SFUserProfile;
  metadata: QuoteMetadata;
  discussionArray: Comment[] | undefined;
}

function RentalTermSheet({
  quote,
  calculation,
  scenarios,
  action,
  userProfile,
  metadata,
  discussionArray,
}: RentalTermSheetProps) {
  const quoteCalculation: { [key: number]: any } = useMemo(() => {
    if (quote && calculation && calculation?.scenarios) {
      for (let scenario of calculation.scenarios) {
        scenario.prepaymentPenaltySelected = quote.prepaymentPenalty;
        scenario.appraisalFee = quote.appraisalFee;
      }

      return {
        ...calculation.scenarios,
        feeLLPAs: calculation.feeLLPAs,
        rateLLPAs: calculation.rateLLPAs,
        overridePricing: calculation.overridePricing,
      };
    } else {
      return { 0: {}, 1: {}, 2: {} };
    }
  }, [calculation, quote]);
  const { exceptionPricingOverride } = quote;
  const { rateLLPAs, feeLLPAs, overridePricing } = quoteCalculation;

  const rateLLPAsArray = useMemo(
    () => bulletListToArray(rateLLPAs),
    [rateLLPAs]
  );

  const feeLLPAsArray = useMemo(() => bulletListToArray(feeLLPAs), [feeLLPAs]);

  const warningsArray = useMemo(() => {
    const allWarnings = calculation.scenarios.filter(
      (scenario) => scenario.warnings
    );
    const stringWarnings = allWarnings.map((scenario) => {
      return scenario.warnings;
    });
    if (stringWarnings) {
      return bulletListToArray(stringWarnings.join());
    }
  }, [calculation.scenarios]);

  const overrideArray = useMemo(() => {
    const overrides = bulletListToArray(overridePricing);
    if (exceptionPricingOverride) {
      for (const [key, value] of Object.entries(exceptionPricingOverride)) {
        if (value !== undefined) {
          if (key === "exceptionType") {
            overrides.unshift(`${overrideLabelMap[key]}: ${value}`);
          }
        }
      }
      if (overrides.length) {
        overrides.unshift(
          `Exception Status: ${metadata.exceptionStatus ?? "Not Submitted"}`
        );
      }
      return overrides;
    }
  }, [overridePricing, metadata.exceptionStatus, exceptionPricingOverride]);

  const quoteDate = useMemo(() => {
    const date = new Date();
    return date.toLocaleDateString();
  }, []);

  const pricingLastUpdated = useMemo(() => {
    return calculation.scenarios[0].pricingUpdateTimestamp ?? "";
  }, [calculation]);

  const rentalScenarios = useMemo(() => {
    return scenarios.filter((x) => x.included === YesNo.Yes);
  }, [scenarios]);

  const rentalQuote: { [key: string]: any } = useMemo(() => {
    return flattenObject(quote) || {};
  }, [quote]);

  const eligibilityArray = React.useMemo(() => {
    const arr: Array<[string, string]> = [];
    const calcScenarios = calculation.scenarios;
    scenarios.forEach((scenario, i) => {
      if (scenario.included !== YesNo.No) {
        arr.push([`Scenario ${i + 1}: `, `${calcScenarios[i].ltvEligibility}`]);
      }
    });
    return arr;
  }, [calculation.scenarios, scenarios]);

  return (
    <Document>
      <RentalSummarySheet
        quote={quote}
        quoteCalculation={quoteCalculation}
        quoteDate={quoteDate}
        rentalScenarios={rentalScenarios}
        action={action}
        userProfile={userProfile}
        metadata={metadata}
      />

      <RentalCalculationSheet
        quote={quote}
        action={action}
        quoteDate={quoteDate}
        productType={quote.productType}
        quoteCalculation={quoteCalculation}
        rentalScenarios={rentalScenarios}
      />

      <RentalDisclosureSheet
        action={action}
        quoteDate={quoteDate}
        rentalQuote={rentalQuote}
        productType={quote.productType}
        quoteCalculation={quoteCalculation}
        rentalScenarios={rentalScenarios}
      />

      {action === "INTERNAL" && (
        <RentalInternalSelectionSheet
          rentalQuote={rentalQuote}
          scenarios={scenarios}
          productType={quote.productType}
          quoteDate={quoteDate}
          pricingLastUpdated={pricingLastUpdated}
          feeLLPAsArray={feeLLPAsArray}
          rateLLPAsArray={rateLLPAsArray}
          overrideArray={overrideArray}
          eligibilityArray={eligibilityArray}
          warningsArray={warningsArray}
          discussionArray={discussionArray}
        />
      )}
    </Document>
  );
}

Font.register({
  family: "Roboto",
  fonts: [
    {
      src: roboto,
    },
    {
      src: robotoBold,
      fontWeight: 700,
    },
    {
      src: robotoItalic,
      fontStyle: "italic",
    },
  ],
});

export default RentalTermSheet;
